const RASTER = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

export class ImageError extends Error {}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Scripts and event handlers have no place in a logo; refuse SVGs that carry them. */
function safeSvg(text: string): boolean {
  return !/<script|<foreignObject|\son\w+\s*=|javascript:|<iframe|<embed/i.test(text);
}

async function rasterize(
  file: Blob,
  opts: { maxSide: number; whiteToTransparent: boolean; jpeg?: boolean },
): Promise<{ bytes: Uint8Array; mime: string }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, opts.maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageError('This browser cannot process images');
  if (opts.jpeg) {
    // A photo has no see-through parts; a white page behind a screenshot keeps it readable.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  if (opts.whiteToTransparent) {
    // A photographed signature has a near-white paper background; make it see-through so it
    // sits cleanly on the report. Soft edge: fully clear above 235, fading down to 190.
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const lum =
        0.299 * (d[i] as number) + 0.587 * (d[i + 1] as number) + 0.114 * (d[i + 2] as number);
      if (lum >= 235) d[i + 3] = 0;
      else if (lum > 190) d[i + 3] = Math.round(((235 - lum) / 45) * 255);
    }
    ctx.putImageData(img, 0, 0);
  }
  const mime = opts.jpeg ? 'image/jpeg' : 'image/png';
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, mime, 0.82));
  if (!blob) throw new ImageError('Could not process that image');
  return { bytes: new Uint8Array(await blob.arrayBuffer()), mime };
}

/**
 * Prepare an uploaded logo, signature or photo: pictures are shrunk and re-encoded (which also
 * drops camera metadata), and an SVG that carries scripts is refused.
 */
export async function prepareImage(
  file: File,
  opts: { maxSide?: number; whiteToTransparent?: boolean; jpeg?: boolean } = {},
): Promise<{ bytes: Uint8Array; mime: string }> {
  // Photos are shrunk before they are kept, so a big camera picture is fine.
  const limit = opts.jpeg ? 25 * 1024 * 1024 : MAX_UPLOAD_BYTES;
  if (file.size > limit)
    throw new ImageError(
      opts.jpeg
        ? 'That picture is too large. Use one under 25 MB.'
        : 'That file is too large. Use one under 4 MB.',
    );
  if (file.type === 'image/svg+xml' && !opts.jpeg) {
    const text = await file.text();
    if (!safeSvg(text)) throw new ImageError('That SVG contains scripts, so it cannot be used.');
    return { bytes: new TextEncoder().encode(text), mime: 'image/svg+xml' };
  }
  if (RASTER.includes(file.type)) {
    return rasterize(file, {
      maxSide: opts.maxSide ?? 900,
      whiteToTransparent: opts.whiteToTransparent ?? false,
      ...(opts.jpeg ? { jpeg: true } : {}),
    });
  }
  throw new ImageError(
    opts.jpeg
      ? 'Use a PNG, JPEG or WebP picture or screenshot.'
      : 'Use a PNG, JPEG or SVG picture.',
  );
}

/** A fresh id for something new, made in the browser. */
export function newUuid(): string {
  return crypto.randomUUID();
}

// ---- a full-page letterhead picture --------------------------------------------------------

/** How much of an A4 page a letterhead picture's own artwork takes at the top and at the foot. */
export interface LetterheadFit {
  headerMm: number;
  footerMm: number;
}

const PAGE_W_MM = 210;
const PAGE_H_MM = 297;

/**
 * Find where a letterhead picture's header and footer artwork end, so the report can be printed in
 * the white space between. A faint watermark in the middle is ignored: only artwork joined to the
 * top or bottom edge counts, ending at the first clear gap.
 */
export async function detectLetterhead(file: Blob): Promise<LetterheadFit | null> {
  const bitmap = await createImageBitmap(file);
  const W = 260;
  const H = Math.max(1, Math.round((W * bitmap.height) / bitmap.width));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.drawImage(bitmap, 0, 0, W, H);
  const px = ctx.getImageData(0, 0, W, H).data;

  const ink: boolean[] = [];
  for (let y = 0; y < H; y++) {
    let n = 0;
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      const lum =
        0.299 * (px[i] as number) + 0.587 * (px[i + 1] as number) + 0.114 * (px[i + 2] as number);
      if (lum < 236) n++;
    }
    ink.push(n >= 2);
  }

  // Where the picture sits on the page: a picture of A4 proportions fills it; another shape is
  // scaled to cover it, as the report engine does.
  const ratio = bitmap.width / bitmap.height / (PAGE_W_MM / PAGE_H_MM);
  const stretch = Math.abs(ratio - 1) < 0.06;
  const scale = stretch ? PAGE_H_MM / H : Math.max(PAGE_W_MM / W, PAGE_H_MM / H);
  const offset = stretch ? 0 : (PAGE_H_MM - H * scale) / 2;
  const mm = (row: number) => offset + row * scale;

  const gapRows = Math.max(2, Math.round(7 / scale));
  const reach = Math.floor(H * 0.45);
  // The artwork joined to the top edge.
  let top = -1;
  let blank = 0;
  for (let y = 0; y < reach; y++) {
    if (ink[y]) {
      top = y;
      blank = 0;
    } else if (top >= 0 && ++blank >= gapRows) break;
  }
  // And the artwork joined to the bottom edge.
  let bottom = H;
  blank = 0;
  let seen = false;
  for (let y = H - 1; y > H - reach; y--) {
    if (ink[y]) {
      bottom = y;
      seen = true;
      blank = 0;
    } else if (seen && ++blank >= gapRows) break;
  }
  return {
    headerMm: top >= 0 ? Math.max(0, mm(top + 1)) : 0,
    footerMm: seen ? Math.max(0, PAGE_H_MM - mm(bottom)) : 0,
  };
}

/**
 * Prepare a letterhead picture: kept at print quality as a JPEG that stays small enough to sync
 * (under 1.9 MB), with the artwork's header and footer measured.
 */
export async function prepareBackground(
  file: File,
): Promise<{ bytes: Uint8Array; mime: string; fit: LetterheadFit | null }> {
  if (!RASTER.includes(file.type)) {
    throw new ImageError('Use a PNG, JPEG or WebP picture of the whole page.');
  }
  if (file.size > 25 * 1024 * 1024) {
    throw new ImageError('That picture is too large. Use one under 25 MB.');
  }
  const bitmap = await createImageBitmap(file);
  let out: { bytes: Uint8Array; mime: string } | null = null;
  for (const side of [3000, 2400, 1900, 1500]) {
    const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ImageError('This browser cannot process images');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.88));
    if (!blob) throw new ImageError('Could not process that picture');
    out = { bytes: new Uint8Array(await blob.arrayBuffer()), mime: 'image/jpeg' };
    if (out.bytes.length <= 1.9 * 1024 * 1024) break;
  }
  if (!out || out.bytes.length > 1.9 * 1024 * 1024) {
    throw new ImageError('That picture is too detailed to keep. Save it smaller and try again.');
  }
  const fit = await detectLetterhead(new Blob([out.bytes as BlobPart], { type: out.mime })).catch(
    () => null,
  );
  return { ...out, fit };
}
